import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BedDouble, CarFront, Search, ShieldCheck, Plane, Compass, MessageCircle, Headphones, Globe2, BadgeCheck, Star, Mail, Phone } from "lucide-react";
import { api, Listing, Location } from "@/lib/api";
import { DateRange } from "@/components/site/DateRange";
import { useSettings } from "@/context/SettingsContext";
import { ListingCard } from "@/components/site/ListingCard";
import { waLink, telLink } from "@/components/site/Footer";
import { STAY_CATEGORIES, VEHICLE_CLASSES } from "@/pages/Listings";

const Kicker = ({ children, light }: { children: React.ReactNode; light?: boolean }) => (
  <p className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] ${light ? "text-brass" : "text-brass-deep"}`}><span className="h-px w-8 bg-brass" />{children}</p>
);

const H2 = ({ children, light }: { children: React.ReactNode; light?: boolean }) => (
  <h2 className={`mt-4 max-w-2xl text-3xl tracking-tight sm:text-4xl ${light ? "text-white" : ""}`}>{children}</h2>
);

const DualSearch = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState<"stay" | "vehicle">("stay");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [guests, setGuests] = useState(2);
  const [city, setCity] = useState("");
  const go = (e: React.FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (tab === "stay") { p.set("guests", String(guests)); nav(`/stays?${p}`); } else { if (city) p.set("city", city); nav(`/vehicles?${p}`); }
  };
  const tabCls = (t: string) => `flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`;
  return (
    <div className="w-full" data-testid="hero-dual-search">
      <div className="mb-3 inline-flex rounded-full border border-navy/10 bg-white p-1">
        <button type="button" onClick={() => setTab("stay")} className={tabCls("stay")} data-testid="search-tab-stay"><BedDouble size={15} /> Stay in Accra</button>
        <button type="button" onClick={() => setTab("vehicle")} className={tabCls("vehicle")} data-testid="search-tab-vehicle"><CarFront size={15} /> Rent a vehicle</button>
      </div>
      <form onSubmit={go} className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-navy/10 bg-navy/10 md:grid-cols-[1fr_2fr_0.7fr_auto]">
        <label className="col-span-2 bg-white px-5 py-3.5 md:col-span-1">
          <span className="label">Location</span>
          {tab === "stay" ? <span className="whitespace-nowrap text-sm font-medium">Accra, Ghana</span> : (
            <select value={city} onChange={(e) => setCity(e.target.value)} className="w-full bg-transparent text-sm font-medium outline-none" data-testid="search-city-select">
              <option value="">All active cities</option><option>El Paso</option><option>Houston</option><option>Accra</option>
            </select>
          )}
        </label>
        <div className="col-span-2 bg-white md:col-span-1">
          <DateRange testid="search-dates" cellClass="px-5 py-3.5" start={start} end={end} labels={tab === "stay" ? ["Check-in", "Check-out"] : ["Pick-up", "Return"]} onChange={(s, e) => { setStart(s); setEnd(e); }} />
        </div>
        <label className="col-span-2 bg-white px-5 py-3.5 md:col-span-1"><span className="label">{tab === "stay" ? "Guests" : "Seats"}</span>
          <input type="number" min={1} value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="w-full bg-transparent text-sm outline-none" data-testid="search-guests" /></label>
        <div className="col-span-2 flex items-center bg-white p-2.5 md:col-span-1">
          <button className="btn-gold h-full w-full rounded-2xl md:w-auto" data-testid="search-submit-button"><Search size={16} /> Search</button>
        </div>
      </form>
    </div>
  );
};

const Hero = () => {
  const { s } = useSettings();
  return (
    <section className="relative isolate overflow-hidden bg-navy text-white" data-testid="home-hero">
      <img src="/images/hero-dark-car.jpg" alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-35" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
      <div className="container-x pb-32 pt-20 lg:pt-28 fade-up">
        <span className="inline-flex items-center gap-2 rounded-full border border-brass/40 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brass" data-testid="hero-badge"><span className="h-1.5 w-1.5 rounded-full bg-brass" /> Stays · Vehicle rentals · Tours · Airport pickups</span>
        <h1 data-testid="hero-title" className="mt-6 max-w-3xl text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl">{s?.hero?.title || "Move With Confidence."}</h1>
        <p className="mt-6 max-w-xl text-base text-white/70 md:text-lg">{s?.hero?.subtitle}</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/stays" className="btn-gold" data-testid="hero-stays-cta">Find a stay <ArrowRight size={16} /></Link>
          <Link to="/vehicles" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-fleet-cta">Explore our fleet</Link>
        </div>
        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-brass/80" data-testid="hero-motto">{s?.tagline || "Delivering Trust."}</p>
      </div>
    </section>
  );
};

const SERVICES = [
  { id: "stays", icon: BedDouble, t: "Accommodation", d: "Studios to family homes in Accra, Ghana.", to: "/stays", img: "/images/hero-stay.jpg" },
  { id: "vehicles", icon: CarFront, t: "Vehicle Rentals", d: "Sedans to luxury SUVs in El Paso, Houston and Accra.", to: "/vehicles", img: "/images/fleet/mercedes-g-wagon-1.jpg" },
  { id: "tours", icon: Compass, t: "Tours & Experiences", d: "Itineraries confirmed with you before booking.", to: "/tours", img: "/images/tours.jpg" },
  { id: "transfers", icon: Plane, t: "Airport Transfers", d: "Live now at Accra International Airport.", to: "/airport-transfers", img: "/images/airport-acc-aerial.jpg" },
];

const ServicePicker = () => (
  <section className="container-x py-16" data-testid="home-services">
    <Kicker>What we do</Kicker>
    <H2>Choose your service</H2>
    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {SERVICES.map((c) => (
        <Link key={c.id} to={c.to} className="group relative flex h-72 flex-col justify-end overflow-hidden rounded-3xl bg-navy p-6 text-white" data-testid={`service-card-${c.id}`}>
          <img src={c.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-[1200ms] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
          <div className="relative">
            <c.icon size={20} className="text-brass" />
            <h3 className="mt-3 text-xl">{c.t}</h3>
            <p className="mt-1 text-sm text-white/70">{c.d}</p>
            <span className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-brass">Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const Featured = ({ kind, title, to, cats }: { kind: "stay" | "vehicle"; title: string; to: string; cats: string[] }) => {
  const [items, setItems] = useState<Listing[] | null>(null);
  useEffect(() => {
    api.get(`/listings/${kind}`).then((r) => {
      const all: Listing[] = r.data;
      const feat = all.filter((x) => x.featured);
      setItems((feat.length >= 4 ? feat : [...feat, ...all.filter((x) => !x.featured)]).slice(0, 4));
    }).catch(() => setItems([]));
  }, [kind]);
  return (
    <section className="container-x py-14" data-testid={`featured-${kind}`}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div><Kicker>{kind === "stay" ? "Featured accommodation" : "Featured vehicles"}</Kicker><H2>{title}</H2></div>
        <Link to={to} className="flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid={`featured-${kind}-view-all`}>View full collection <ArrowRight size={15} /></Link>
      </div>
      <div className="mb-8 flex flex-wrap gap-2">
        {cats.map((c) => <Link key={c} to={`${to}?category=${encodeURIComponent(c)}`} className="rounded-full border border-navy/15 px-4 py-1.5 text-sm transition-colors hover:border-brass hover:bg-brass-soft" data-testid={`featured-${kind}-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</Link>)}
      </div>
      {items && items.length > 0 ? (
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">{items.map((it, i) => <ListingCard key={it.id} item={it} kind={kind} i={i} />)}</div>
      ) : items ? (
        <p className="rounded-2xl border border-dashed border-navy/15 p-8 text-sm text-navy/60" data-testid={`featured-${kind}-empty`}>Verified {kind === "stay" ? "properties" : "vehicles"} are being added. Contact our team and we will help you directly.</p>
      ) : null}
    </section>
  );
};

const GuestServices = () => (
  <section className="relative bg-navy py-20 text-white grain" data-testid="home-guest-services">
    <div className="container-x relative">
      <Kicker light>Guest services & experiences</Kicker>
      <H2 light>From arrival to departure, a real team on your side.</H2>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {[{ icon: Plane, t: "Airport pickups", d: "Available now at Accra International Airport. Houston and Liberia coming soon.", to: "/airport-transfers", id: "transfers" },
          { icon: Headphones, t: "Guest support", d: "Live for guests in Accra and the U.S. — before, during and after your stay.", to: "/support", id: "support" },
          { icon: Compass, t: "Tours & experiences", d: "Tell us what you'd like to see. We confirm itinerary and pricing first.", to: "/tours", id: "tours" }].map((c) => (
          <Link key={c.id} to={c.to} className="group rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors hover:border-brass/50" data-testid={`home-guest-${c.id}`}>
            <c.icon size={22} className="text-brass" />
            <h3 className="mt-6 text-2xl">{c.t}</h3>
            <p className="mt-3 text-sm text-white/60">{c.d}</p>
            <span className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-brass">Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="container-x py-20" data-testid="home-why">
    <Kicker>Why choose Trust Logistics</Kicker>
    <H2>Delivering trust, one booking at a time.</H2>
    <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      {[{ icon: BadgeCheck, t: "Personally reviewed", d: "Every request is reviewed by our team before it is confirmed." },
        { icon: ShieldCheck, t: "Honest pricing", d: "Prices are calculated on our server. No payment is taken before approval." },
        { icon: Headphones, t: "Real people, 24/7", d: "Support inquiries are received around the clock and answered by our team." },
        { icon: Globe2, t: "Africa & the U.S.", d: "One trusted brand connecting Accra, Texas and beyond." }].map((w) => (
        <div key={w.t} className="border-t border-brass/40 pt-6" data-testid={`why-${w.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <w.icon size={22} className="text-brass-deep" />
          <h3 className="mt-4 font-sans text-lg font-semibold">{w.t}</h3>
          <p className="mt-2 text-sm text-navy/60">{w.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const LocCol = ({ title, locs, testid }: { title: string; locs: Location[]; testid: string }) => (
  <div className="card-lux p-6 sm:p-8" data-testid={testid}>
    <h3 className="text-xl">{title}</h3>
    <ul className="mt-5 divide-y divide-navy/10">
      {locs.map((l) => (
        <li key={l.id} className="flex items-center justify-between gap-3 py-3" data-testid={`location-${l.service}-${l.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <span className="font-medium">{l.title}</span>
          {l.status === "available"
            ? <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-700">Available now</span>
            : <span className="rounded-full bg-brass-soft px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-brass-deep">Coming soon</span>}
        </li>
      ))}
    </ul>
  </div>
);

const Locations = () => {
  const [locs, setLocs] = useState<Location[]>([]);
  useEffect(() => {
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => setLocs([]));
  }, []);
  if (!locs.length) return null;
  return (
    <section className="bg-stone-canvas py-20" data-testid="home-locations">
      <div className="container-x">
        <Kicker>Service locations</Kicker>
        <H2>Where we operate today — and where we're going.</H2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <LocCol title="Accommodation" locs={locs.filter((l) => l.service === "stays")} testid="locations-stays" />
          <LocCol title="Vehicle rentals" locs={locs.filter((l) => l.service === "vehicles")} testid="locations-vehicles" />
        </div>
        <p className="mt-6 text-xs text-navy/50">Coming Soon markets are not bookable yet. <Link to="/coming-soon" className="underline">Register your interest</Link> to hear when they open.</p>
      </div>
    </section>
  );
};

const ComingSoonStrip = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api.get("/coming-soon").then((r) => setItems(r.data.slice(0, 3))).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="container-x py-20" data-testid="home-coming-soon">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><Kicker>Coming soon</Kicker><H2>Expansion projects</H2></div>
        <Link to="/coming-soon" className="flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid="home-coming-soon-all">See the roadmap <ArrowRight size={15} /></Link>
      </div>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {items.map((c) => (
          <Link key={c.id} to={c.title?.toLowerCase().includes("gant") ? "/estates" : "/coming-soon"} className="card-lux p-7 transition-transform duration-300 hover:-translate-y-1" data-testid={`home-coming-${c.id}`}>
            <span className="font-mono text-[10px] uppercase tracking-widest text-brass-deep">{c.category || "Coming soon"}</span>
            <h3 className="mt-3 text-xl">{c.title}</h3>
            <p className="mt-2 text-sm text-navy/60">{c.description}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const GantFeature = () => (
  <section className="relative isolate overflow-hidden bg-navy text-white" data-testid="home-gant-estate">
    <img src="/images/estates/sunset.jpg" alt="Concept render — Gant Estates at sunset" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-45" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/80 to-transparent" />
    <div className="container-x py-24">
      <Kicker light>Gant Estates · Planned launch November 2027</Kicker>
      <H2 light>A new standard for modern living in Liberia.</H2>
      <p className="mt-5 max-w-xl text-sm text-white/70 md:text-base">A planned gated residential community for diaspora families, professionals and corporate tenants. All images are concept renders; plans are subject to change.</p>
      <Link to="/estates" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brass px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-[#D4B46B]" data-testid="home-gant-cta">Discover Gant Estates <ArrowRight size={16} /></Link>
    </div>
  </section>
);

const Reviews = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api.get("/reviews").then((r) => setItems(r.data.slice(0, 3))).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="container-x py-20" data-testid="home-reviews">
      <Kicker>Guest reviews</Kicker>
      <H2>What our guests say</H2>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {items.map((r) => (
          <figure key={r.id} className="card-lux p-7">
            <div className="flex gap-0.5 text-brass">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
            <blockquote className="mt-4 text-sm text-navy/75">"{r.comment || r.text}"</blockquote>
            <figcaption className="mt-4 text-sm font-semibold">{r.name}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

const FounderNote = () => {
  const { s } = useSettings();
  const f = s?.founder;
  return (
    <section className="container-x pb-20" data-testid="home-founder">
      <div className="grid items-center gap-6 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-[96px_1fr_auto] sm:p-8">
        {f?.approved && f?.portrait_url ? <img src={f.portrait_url} alt={f.name} className="h-24 w-24 rounded-2xl object-cover object-top" /> : <span />}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brass-deep">About the founder</p>
          <p className="mt-2 font-serif text-lg font-bold">{f?.name || "Stanley Teddy Gant"}</p>
          <p className="mt-1 max-w-2xl text-sm text-navy/65">Currently serving in the U.S. Army, a husband and father, Stanley is building Trust Logistics LLC around dependable service, hospitality, global connections and long-term business development.</p>
        </div>
        <Link to="/about" className="btn-ghost" data-testid="home-founder-link">Read his story <ArrowRight size={14} /></Link>
      </div>
    </section>
  );
};

const Contact = () => {
  const { s } = useSettings();
  const wa = s?.phones?.[0];
  return (
    <section className="container-x pb-24" data-testid="home-contact">
      <div className="grid gap-8 rounded-3xl bg-brass-soft p-8 sm:p-12 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <Kicker>Contact & support</Kicker>
          <H2>Talk to our team.</H2>
          <p className="mt-3 text-sm text-navy/65 md:text-base">Support inquiries are received {s?.support_hours || "24/7"}. Messages are answered by a real person — never a bot pretending to be one.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {wa && <a href={waLink(wa)} target="_blank" rel="noreferrer" className="btn-navy" data-testid="home-whatsapp-cta"><MessageCircle size={16} /> WhatsApp us</a>}
            <Link to="/support" className="btn-ghost" data-testid="home-support-cta">Open a support ticket</Link>
          </div>
        </div>
        <ul className="space-y-3 self-center text-sm text-navy/75">
          {s?.support_email && <li className="flex items-center gap-2.5"><Mail size={15} className="text-brass-deep" /><a href={`mailto:${s.support_email}`} className="hover:underline" data-testid="home-contact-support-email">Customer service: {s.support_email}</a></li>}
          {s?.booking_email && <li className="flex items-center gap-2.5"><Mail size={15} className="text-brass-deep" /><a href={`mailto:${s.booking_email}`} className="hover:underline" data-testid="home-contact-booking-email">Bookings: {s.booking_email}</a></li>}
          {s?.business_phone && <li className="flex items-center gap-2.5"><Phone size={15} className="text-brass-deep" /><a href={telLink(s.business_phone)} className="hover:underline" data-testid="home-contact-phone">Call: {s.business_phone}</a></li>}
          {s?.phones?.map((p, i) => <li key={i} className="flex items-center gap-2.5"><MessageCircle size={15} className="text-brass-deep" /><a href={waLink(p)} target="_blank" rel="noreferrer" className="hover:underline" data-testid={`home-contact-wa-${i}`}>WhatsApp {p.label}: {p.number}</a></li>)}
        </ul>
      </div>
    </section>
  );
};

export default function Home() {
  const { s } = useSettings();
  return (
    <div>
      <Hero />
      <div className="container-x relative z-10 -mt-20 mb-4"><div className="rounded-3xl bg-white/95 p-4 shadow-[0_30px_70px_-35px_rgba(0,0,0,0.6)] backdrop-blur sm:p-6"><DualSearch /></div></div>
      <ServicePicker />
      <Featured kind="stay" title={s?.home?.stays_heading || "Stays in Accra"} to="/stays" cats={STAY_CATEGORIES.slice(0, 6)} />
      <Featured kind="vehicle" title={s?.home?.vehicles_heading || "Our vehicle collection"} to="/vehicles" cats={VEHICLE_CLASSES.slice(0, 6)} />
      <GuestServices />
      <WhyUs />
      <Locations />
      <ComingSoonStrip />
      <GantFeature />
      <Reviews />
      <div className="pt-20" />
      <FounderNote />
      <Contact />
    </div>
  );
}
